import type { TrainingSessionListView, TrainingSessionProgress } from "@/api/training";
import { sessionSimpleStatus, type SessionStatusKey } from "@/modules/training/public";
import { parseUtcMs } from "@/utils/datetime";
import { formatNumber } from "@/utils/formats";
import { formatEtaSeconds } from "./sessionDetail/trainingSessionDetailFormat";
import {
  submittedDatasetLabel,
  submittedDatasetUsage,
  trainingProgressStageLabel,
} from "./trainingSessionFormat";

// 세션 hover 카드가 그리는 줄. 표의 다른 열에 없는 값만 만든다. 목록 행 하나에서 계산하고 서버를
// 다시 부르지 않는다.

export type SessionCardFactKey =
  | "stage"
  | "progress"
  | "eta"
  | "saved"
  | "elapsed"
  | "duration"
  | "schedule"
  | "init";

export type SessionCardFact = {
  key: SessionCardFactKey;
  label: string;
  value: string;
};

export type SessionCardDataset = {
  name: string;
  // 역할·비율·타깃. 제출 위저드의 입력 요약과 같은 어휘다. 예) "train · weight 4 · target"
  usage: string;
};

export type SessionCardDatasets = {
  entries: readonly SessionCardDataset[];
  // entries 에 싣지 못한 나머지 입력 수.
  extraCount: number;
};

// 끝났지만 성공하지 못한 상태. 이 상태에서는 "어디까지 갔고 무엇이 남았나"가 정보다.
const STOPPED_STATUS_KEYS: ReadonlySet<SessionStatusKey> = new Set([
  "failed",
  "action_required",
  "cancelled",
]);

// 상류 init 어휘 → 표시 문구. 제출 위저드의 시작점 선택지와 같은 말을 쓴다.
const INIT_STRATEGY_LABELS: Record<string, string> = {
  scratch: "처음부터 (scratch)",
  finetune_from_policy: "Policy에서 (fine tune)",
  finetune_from_checkpoint: "Checkpoint 이어서 (retrain)",
  resume_from_checkpoint: "이어서 학습 (resume)",
};

/**
 * 목록 행에서 카드 줄을 만든다. 상태에 따라 뜻이 있는 줄만 낸다.
 *
 * - 실행 중: 단계 순번, 진행량, 남은 시간, 저장 지점, 경과 시간.
 * - 멈춤(실패·취소·조치 필요): 멈춘 지점의 진행량, 저장 지점, 소요 시간.
 * - 성공: 소요 시간.
 * - 대기: 진행·시간 줄이 없다.
 *
 * 예약 출처와 시작점은 상태와 무관하게 값이 있으면 낸다.
 *
 * `nowMs` 는 경과 시간을 재는 기준 시각이다. 목록은 폴링하지 않는다. 그래서 진행량과 남은 시간은
 * 목록을 받은 시점의 값이다.
 */
export function buildSessionCardFacts(
  view: TrainingSessionListView,
  nowMs: number,
): SessionCardFact[] {
  const statusKey = sessionSimpleStatus(view.status).key;
  const running = statusKey === "in_progress";
  const stopped = STOPPED_STATUS_KEYS.has(statusKey);
  const progress = view.progress;
  const facts: SessionCardFact[] = [];

  if (progress !== null && (running || stopped)) {
    const amount = progressAmount(progress);
    if (amount !== null) {
      facts.push({ key: "progress", label: "진행", value: amount });
    }
  }
  if (progress !== null && running) {
    const stage = stageFact(progress);
    if (stage !== null) {
      facts.push(stage);
    }
  }
  // 남은 시간은 마지막 보고 시점의 값이다. 종료한 세션에도 남으므로 실행 중일 때만 읽는다.
  if (progress !== null && running && progress.eta_seconds !== null) {
    facts.push({
      key: "eta",
      label: "남은 시간",
      value: `약 ${formatEtaSeconds(progress.eta_seconds)}`,
    });
  }
  if (progress !== null && (running || stopped) && progress.last_saved_epoch !== null) {
    facts.push({ key: "saved", label: "저장", value: `epoch ${progress.last_saved_epoch}` });
  }

  const time = timeFact(view, running, nowMs);
  if (time !== null) {
    facts.push(time);
  }
  if (view.schedule_origin !== null) {
    facts.push({ key: "schedule", label: "예약", value: view.schedule_origin.schedule_name });
  }
  if (view.init_strategy !== null) {
    facts.push({
      key: "init",
      label: "시작",
      // 모르는 어휘는 감추지 않고 그대로 보여준다.
      value: INIT_STRATEGY_LABELS[view.init_strategy] ?? view.init_strategy,
    });
  }
  return facts;
}

/**
 * 카드의 Dataset 묶음. 서버가 준 상위 입력의 이름과 사용 방식을 내고, 나머지는 개수로 접는다.
 * 제출 저널이 없는 세션은 null 이다.
 */
export function buildSessionCardDatasets(
  view: TrainingSessionListView,
): SessionCardDatasets | null {
  if (view.leading_datasets.length === 0) {
    return null;
  }
  return {
    entries: view.leading_datasets.map((entry) => ({
      name: submittedDatasetLabel(entry),
      usage: submittedDatasetUsage(entry),
    })),
    extraCount: Math.max(0, view.dataset_count - view.leading_datasets.length),
  };
}

// 단계가 하나뿐이면 순번에 정보가 없다. 두 단계 이상인 파이프라인에서만 낸다.
function stageFact(progress: TrainingSessionProgress): SessionCardFact | null {
  if (progress.step_order === null || progress.step_total === null || progress.step_total <= 1) {
    return null;
  }
  const order = `${progress.step_order}/${progress.step_total}`;
  return {
    key: "stage",
    label: "단계",
    value:
      progress.stage === null ? order : `${order} · ${trainingProgressStageLabel(progress.stage)}`,
  };
}

// 진행량과 단위. 단위는 단계 종류가 정한다. train 은 보고한 unit, eval 은 에피소드다.
function progressAmount(progress: TrainingSessionProgress): string | null {
  if (progress.completed === null || progress.total === null || progress.total <= 0) {
    return null;
  }
  const amount = `${formatNumber(progress.completed)} / ${formatNumber(progress.total)}`;
  if (trainingProgressStageLabel(progress.stage ?? "") === "Evaluation") {
    return `${amount} 에피소드`;
  }
  if (progress.unit === null) {
    return amount;
  }
  // step 단위 학습이 epoch 도 함께 보고하면 덧붙인다. epoch 단위에서는 진행량이 곧 epoch 다.
  return progress.unit === "step" && progress.current_epoch !== null
    ? `${amount} step · epoch ${progress.current_epoch}`
    : `${amount} ${progress.unit}`;
}

// 끝난 세션은 소요 시간, 실행 중인 세션은 지금까지의 경과 시간이다. 시작하지 않았으면 없다.
function timeFact(
  view: TrainingSessionListView,
  running: boolean,
  nowMs: number,
): SessionCardFact | null {
  const startedMs = parseUtcMs(view.started_at);
  if (startedMs === null) {
    return null;
  }
  const finishedMs = parseUtcMs(view.finished_at);
  if (finishedMs !== null) {
    return {
      key: "duration",
      label: "소요",
      value: formatEtaSeconds((finishedMs - startedMs) / 1000),
    };
  }
  if (!running) {
    return null;
  }
  return { key: "elapsed", label: "경과", value: formatEtaSeconds((nowMs - startedMs) / 1000) };
}
